import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Grid,
  Alert,
} from "@mui/material";
import { DateTime } from "luxon";
import { getErrorMessage } from "utils/errorHelpers";
import type { Tier } from "client/api-v2";
import {
  type TransitionToComplimentaryRequestDto,
  ComplimentaryType,
  AutoRenewPeriod,
  LifecycleType,
} from "client/api-v2";
import DatePickerInput from "components/DatePickerInput/DatePickerInput";
import { createEnumChangeHandler } from "../utils/enum-utils";

interface TransitionToComplimentaryDialogProps {
  open: boolean;
  subscriptionName: string;
  currentTier: Tier;
  currentLifecycleType: LifecycleType;
  handleClose: () => void;
  submitTransition: (request: TransitionToComplimentaryRequestDto) => Promise<void>;
}

export function TransitionToComplimentaryDialog({
  open,
  subscriptionName,
  currentTier,
  currentLifecycleType,
  handleClose,
  submitTransition,
}: TransitionToComplimentaryDialogProps) {
  const [complimentaryType, setComplimentaryType] = useState<ComplimentaryType>(ComplimentaryType.Staff);
  const [autoRenewPeriod, setAutoRenewPeriod] = useState<AutoRenewPeriod | undefined>(undefined);
  const [termStartDate, setTermStartDate] = useState<DateTime>(DateTime.now().startOf("day"));
  const [termEndDate, setTermEndDate] = useState<DateTime | undefined>(
    DateTime.now().plus({ years: 1 }).minus({ days: 1 }).startOf("day")
  );
  const [reason, setReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setComplimentaryType(ComplimentaryType.Staff);
      setAutoRenewPeriod(undefined);
      setTermStartDate(DateTime.now().startOf("day"));
      setTermEndDate(DateTime.now().plus({ years: 1 }).minus({ days: 1 }).startOf("day"));
      setReason("");
      setError(undefined);
    }
  }, [open]);

  // When complimentary type changes
  useEffect(() => {
    if (complimentaryType === ComplimentaryType.Infrastructure) {
      // Default to Monthly auto-renewal
      if (!autoRenewPeriod) {
        setAutoRenewPeriod(AutoRenewPeriod.Monthly);
      }
    } else {
      setAutoRenewPeriod(undefined);
      // Ensure term end date is set for non-Infrastructure types
      if (!termEndDate) {
        setTermEndDate(DateTime.now().plus({ years: 1 }).minus({ days: 1 }).startOf("day"));
      }
    }
  }, [complimentaryType, autoRenewPeriod, termEndDate]);

  const showAutoRenewal = complimentaryType === ComplimentaryType.Infrastructure;
  const showTermEndDate = !showAutoRenewal || !autoRenewPeriod;

  // Validation logic
  const isValid = useMemo(() => {
    if (!reason.trim() || reason.length > 500) return false;
    if (!termStartDate) return false;

    if (complimentaryType === ComplimentaryType.Infrastructure) {
      if (!autoRenewPeriod) return false;
    }

    if (showTermEndDate) {
      if (!termEndDate) return false;
      if (termEndDate <= termStartDate) return false;
      if (termEndDate > DateTime.now().startOf("day").plus({ years: 1 })) return false;
    }

    return true;
  }, [complimentaryType, autoRenewPeriod, termStartDate, termEndDate, reason, showTermEndDate]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(undefined);

    try {
      const request: TransitionToComplimentaryRequestDto = {
        complimentaryType,
        autoRenewPeriod: showAutoRenewal ? autoRenewPeriod : undefined,
        termStartDate: termStartDate.toISODate() ?? "",
        termEndDate: showTermEndDate && termEndDate ? termEndDate.toISODate() : undefined,
        reason: reason.trim(),
      };

      await submitTransition(request);
      handleClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setComplimentaryType(ComplimentaryType.Staff);
    setAutoRenewPeriod(undefined);
    setTermStartDate(DateTime.now().startOf("day"));
    setTermEndDate(DateTime.now().plus({ years: 1 }).minus({ days: 1 }).startOf("day"));
    setReason("");
    setError(undefined);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Transition to Complimentary</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Current Context Section */}
          <Grid size={12}>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Subscription:</strong> {subscriptionName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                <strong>Current Lifecycle:</strong> {currentLifecycleType}
              </Typography>
            </Box>
          </Grid>

          {/* Warnings */}
          {currentLifecycleType === LifecycleType.Paid && (
            <Grid size={12}>
              <Alert severity="info">
                <strong>Note:</strong> Billing system associations (Zuora, Salesforce) will be automatically cleared
                during this transition. You may need to manually cancel or update the subscription in the external
                billing system to avoid continued charges.
              </Alert>
            </Grid>
          )}

          <Grid size={12}>
            <Alert severity="info">
              The subscription will remain on its current <strong>{currentTier}</strong> tier plan. To change the tier,
              use the "Edit Subscription" action after transitioning. Use "Edit Entitlement Overrides" to customize
              entitlements.
            </Alert>
          </Grid>

          {/* Error Display */}
          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {/* Complimentary Type Selection */}
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>Complimentary Type</InputLabel>
              <Select
                value={complimentaryType}
                label="Complimentary Type"
                onChange={createEnumChangeHandler(ComplimentaryType, setComplimentaryType)}
              >
                {Object.values(ComplimentaryType).map((typeValue) => (
                  <MenuItem key={typeValue} value={typeValue}>
                    {typeValue}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Auto-Renewal Period (shown for Infrastructure) */}
          {showAutoRenewal && (
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Auto-Renewal Period</InputLabel>
                <Select
                  value={autoRenewPeriod ?? ""}
                  label="Auto-Renewal Period"
                  onChange={createEnumChangeHandler(AutoRenewPeriod, setAutoRenewPeriod)}
                >
                  {Object.values(AutoRenewPeriod).map((periodValue) => (
                    <MenuItem key={periodValue} value={periodValue}>
                      {periodValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Term Start Date */}
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <DatePickerInput
              label="Term Start Date"
              value={termStartDate}
              onChange={(date) => setTermStartDate(date ?? DateTime.now().startOf("day"))}
              showLocalConversion={true}
              required
            />
          </Grid>

          {/* Term End Date (conditional) */}
          {showTermEndDate && (
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <DatePickerInput
                label="Term End Date"
                value={termEndDate}
                onChange={(date) => setTermEndDate(date ?? undefined)}
                showLocalConversion={true}
                minDate={termStartDate.plus({ days: 1 })}
                maxDate={DateTime.now().startOf("day").plus({ years: 1 }).minus({ days: 1 })}
                required
              />
            </Grid>
          )}

          {/* Reason */}
          <Grid size={12}>
            <TextField
              autoFocus
              required
              fullWidth
              multiline
              rows={4}
              label="Reason"
              placeholder="Please provide a reason for this transition (e.g., staff member, partnership agreement, etc.)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={!!error && !reason.trim()}
              helperText={
                reason.length > 500
                  ? `Reason is too long (${reason.length}/500 characters)`
                  : !reason.trim() && !!error
                  ? "Reason is required"
                  : ""
              }
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !isValid}>
          {isSubmitting ? "Transitioning..." : "Transition to Complimentary"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
